import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Crosshair } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { SERVICE_CATALOG } from '@/lib/domain';
import { detectCity, getCurrentPosition } from '@/lib/geo';
import Spinner from '@/components/Spinner';
import AuthLayout from './AuthLayout';
import styles from './auth.module.css';

export default function ProviderRequest() {
  const navigate = useNavigate();
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [city, setCity] = useState('');
  const [experience, setExperience] = useState('0');
  const [description, setDescription] = useState('');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [cinPhotoUrl, setCinPhotoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState('');

  // Prefill from the profile created during signup.
  useEffect(() => {
    void (async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login', { replace: true });
        return;
      }
      const { data } = await supabase
        .from('profiles')
        .select('full_name, phone, cin_photo_url')
        .eq('id', user.id)
        .maybeSingle();
      if (data) {
        if (data.full_name) setFullName(data.full_name);
        if (data.phone) setPhone(data.phone);
        if (data.cin_photo_url) setCinPhotoUrl(data.cin_photo_url);
      }
    })();
  }, [navigate]);

  const toggleService = (id: string) => {
    setSelectedServices((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id]
    );
  };

  const useCurrentCity = async () => {
    setError('');
    setLocating(true);
    try {
      const coords = await getCurrentPosition();
      const detected = await detectCity(coords);
      if (!detected) {
        setError('Impossible de détecter votre ville. Saisissez-la manuellement.');
        return;
      }
      setCity(detected);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Localisation indisponible.');
    } finally {
      setLocating(false);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    if (!fullName.trim()) return setError('Veuillez saisir votre nom complet.');
    if (!phone.trim()) return setError('Veuillez saisir votre numéro de téléphone.');
    if (!city.trim()) return setError('Veuillez saisir votre ville.');
    if (selectedServices.length === 0) return setError('Veuillez sélectionner au moins un service.');

    setLoading(true);
    try {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) {
        setError('Erreur : utilisateur non trouvé.');
        return;
      }

      const { error: appError } = await supabase.from('provider_applications').insert({
        user_id: user.id,
        full_name: fullName.trim(),
        phone: phone.trim(),
        city: city.trim(),
        services: selectedServices,
        experience_years: Number.parseInt(experience, 10) || 0,
        description: description.trim(),
        document_url: cinPhotoUrl, // reuse the CIN photo captured at signup
        status: 'pending',
      });

      if (appError) {
        setError(appError.message);
        return;
      }

      await supabase.from('profiles').update({ city: city.trim() }).eq('id', user.id);
      navigate('/app', { replace: true });
    } catch {
      setError('Une erreur est survenue. Réessayez.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Devenir prestataire" subtitle="Complétez votre demande d'inscription.">
      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {error ? (
          <p className="alert alert--error" role="alert">
            {error}
          </p>
        ) : null}

        <div className="field">
          <label className="label" htmlFor="fullName">
            Nom complet <span className="req">*</span>
          </label>
          <input
            id="fullName"
            className="input"
            placeholder="Jean Dupont"
            value={fullName}
            onChange={(event) => setFullName(event.target.value)}
            disabled={loading}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="phone">
            Téléphone <span className="req">*</span>
          </label>
          <input
            id="phone"
            className="input"
            type="tel"
            placeholder="+212 6XX XXX XXX"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            disabled={loading}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="city">
            Ville <span className="req">*</span>
          </label>
          <input
            id="city"
            className="input"
            placeholder="Votre ville"
            value={city}
            onChange={(event) => setCity(event.target.value)}
            disabled={loading || locating}
          />
          <button
            type="button"
            className={styles.locationBtn}
            onClick={() => void useCurrentCity()}
            disabled={loading || locating}
          >
            {locating ? <Spinner size={16} color="var(--navy)" /> : <Crosshair size={16} />}
            {locating ? 'Détection en cours...' : 'Utiliser ma position actuelle'}
          </button>
        </div>

        <div className="field">
          <span className="label">
            Services offerts <span className="req">*</span>
          </span>
          <span className="hint">Sélectionnez au moins un service</span>
          <div className={styles.tagGrid}>
            {SERVICE_CATALOG.map((service) => (
              <button
                key={service.id}
                type="button"
                className={
                  selectedServices.includes(service.id) ? 'chip chip--on' : 'chip'
                }
                onClick={() => toggleService(service.id)}
                aria-pressed={selectedServices.includes(service.id)}
                disabled={loading}
              >
                {service.name}
              </button>
            ))}
          </div>
        </div>

        <div className="field">
          <label className="label" htmlFor="experience">
            Années d&apos;expérience
          </label>
          <input
            id="experience"
            className="input"
            type="number"
            min="0"
            value={experience}
            onChange={(event) => setExperience(event.target.value)}
            disabled={loading}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="description">
            Description
          </label>
          <textarea
            id="description"
            className="textarea"
            placeholder="Décrivez votre expérience et vos compétences..."
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={loading}
          />
        </div>

        <button type="submit" className="btn btn--primary btn--block" disabled={loading}>
          {loading ? 'Envoi en cours...' : 'Envoyer la demande'}
        </button>

        <p className={styles.notice}>
          Votre demande sera vérifiée par l&apos;administration. Vous recevrez une notification une
          fois approuvée.
        </p>
      </form>
    </AuthLayout>
  );
}
